/**
 * PDP 产品锚点吸顶栏
 * body[data-omt-pdp-anchor=idle|active]：顶替 Header 栈；与 OMTHeaderOffsets 协同
 *
 * 移动端抖动修复要点：
 * 1. 显隐滞回，避免阈值附近反复切换
 * 2. 切换时用 refreshFromPdpAnchor（禁止 scrollTo 补偿）
 * 3. omt:header-offsets 只更新选中态，不回写显隐
 */
(function () {
  'use strict';

  if (customElements.get('oc-mr-product-anchor-nav')) return;

  // 滞回：展开/收起阈值分离，避免临界抖动
  var SHOW_SCROLL_MIN = 48;
  var HIDE_SCROLL_MAX = 8;

  class OcMrProductAnchorNav extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.bar = this.querySelector('[data-pdp-anchor-bar]');
      this.links = this.querySelectorAll('[data-pdp-anchor-link]');
      this.isActive = false;
      this.scrollRaf = null;
      this.clickLockLink = null;
      this.clickLockUntil = 0;
      this._onScroll = this.requestUpdate.bind(this);
      this._onOffsets = this.onOffsets.bind(this);

      document.body.classList.add('has-omt-pdp-anchor');
      if (!document.body.getAttribute('data-omt-pdp-anchor')) {
        document.body.setAttribute('data-omt-pdp-anchor', 'idle');
      }

      this.links.forEach(
        function (link) {
          link.addEventListener('click', this.onLinkClick.bind(this, link));
        }.bind(this)
      );

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll);
      document.addEventListener('omt:header-offsets', this._onOffsets);
      document.addEventListener('shopify:section:load', this._onScroll);

      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
      document.removeEventListener('omt:header-offsets', this._onOffsets);
      document.removeEventListener('shopify:section:load', this._onScroll);

      document.documentElement.style.setProperty('--omt-pdp-anchor-height', '0px');
      document.body.classList.remove('has-omt-pdp-anchor');
      document.body.setAttribute('data-omt-pdp-anchor', 'idle');

      if (window.OMTHeaderOffsets && typeof window.OMTHeaderOffsets.refreshFromPdpAnchor === 'function') {
        window.OMTHeaderOffsets.refreshFromPdpAnchor();
      } else if (window.OMTHeaderOffsets && typeof window.OMTHeaderOffsets.refresh === 'function') {
        window.OMTHeaderOffsets.refresh();
      }
      this._bound = false;
    }

    getScrollY() {
      return window.pageYOffset || document.documentElement.scrollTop || 0;
    }

    getAnchorOffset() {
      var value = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--omt-anchor-offset'),
        10
      );
      return isNaN(value) ? 240 : value;
    }

    requestUpdate() {
      if (this.scrollRaf) return;
      this.scrollRaf = window.requestAnimationFrame(
        function () {
          this.scrollRaf = null;
          this.update();
        }.bind(this)
      );
    }

    onOffsets() {
      // 只刷新选中态，避免 offsets → setActive → refresh → offsets 反馈环
      if (this.scrollRaf) return;
      this.scrollRaf = window.requestAnimationFrame(
        function () {
          this.scrollRaf = null;
          this.updateActiveLink();
        }.bind(this)
      );
    }

    setActive(next) {
      if (next === this.isActive) return;

      this.isActive = next;
      document.body.setAttribute('data-omt-pdp-anchor', next ? 'active' : 'idle');
      this.writeAnchorHeight();

      if (window.OMTHeaderOffsets && typeof window.OMTHeaderOffsets.refreshFromPdpAnchor === 'function') {
        window.OMTHeaderOffsets.refreshFromPdpAnchor();
      } else if (window.OMTHeaderOffsets && typeof window.OMTHeaderOffsets.refresh === 'function') {
        window.OMTHeaderOffsets.refresh();
      }
    }

    writeAnchorHeight() {
      var height = 0;
      if (this.isActive && this.bar) {
        height = this.bar.offsetHeight || 56;
      }
      document.documentElement.style.setProperty(
        '--omt-pdp-anchor-height',
        Math.max(Math.round(height), 0) + 'px'
      );
    }

    update() {
      var scrollY = this.getScrollY();
      var nextActive;
      if (this.isActive) {
        nextActive = scrollY > HIDE_SCROLL_MAX;
      } else {
        nextActive = scrollY > SHOW_SCROLL_MIN;
      }
      this.setActive(nextActive);
      this.updateActiveLink();
    }

    updateActiveLink() {
      if (!this.links.length) return;

      // 点击锁定期间保持用户选中项，避免 smooth scroll 途中被 spy 改掉
      if (this.clickLockLink && Date.now() < this.clickLockUntil) {
        this.links.forEach(
          function (link) {
            link.classList.toggle('is-active', link === this.clickLockLink);
          }.bind(this)
        );
        return;
      }
      this.clickLockLink = null;

      var offset = this.getAnchorOffset();
      var current = null;
      // 取「已越过激活线」且 top 最大的目标（最靠近激活线），
      // 不能按导航顺序取最后一个：Reviews 等区块若在 DOM 中位于 FAQ 之上，
      // 看 FAQ 时 Reviews.top 已为负，按顺序会误高亮 Reviews。
      var bestTop = -Infinity;

      this.links.forEach(function (link) {
        var id = link.getAttribute('data-target-id');
        if (!id) return;
        var target = document.getElementById(id);
        if (!target) return;
        var top = target.getBoundingClientRect().top;
        if (top <= offset + 10 && top >= bestTop) {
          bestTop = top;
          current = link;
        }
      });

      this.links.forEach(function (link) {
        link.classList.toggle('is-active', link === current);
      });

      if (!current && this.links[0]) {
        this.links[0].classList.add('is-active');
      }
    }

    onLinkClick(link, e) {
      e.preventDefault();
      var id = link.getAttribute('data-target-id');
      if (!id) return;
      var target = document.getElementById(id);
      if (!target) return;

      this.clickLockLink = link;
      this.clickLockUntil = Date.now() + 1200;
      this.links.forEach(function (el) {
        el.classList.toggle('is-active', el === link);
      });

      var offset = this.getAnchorOffset();
      var rect = target.getBoundingClientRect();
      var scrollTop = this.getScrollY();
      window.scrollTo({
        top: Math.max(0, rect.top + scrollTop - offset),
        behavior: 'smooth',
      });
    }
  }

  customElements.define('oc-mr-product-anchor-nav', OcMrProductAnchorNav);
})();
